import { useEffect, useRef } from 'react'

import { type FormikProps } from 'formik'
import { useSelector } from 'react-redux'

import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import {
  parsePercentageOrZero,
  round2,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import { ExpenseTypes } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseLineItemFormSchema } from './ExpenseLineItemForm'
import { isPresetTax } from '../ExpenseTaxSelect'

interface UseRecomputeTaxAmountParams {
  values: ExpenseLineItemFormSchema
  setFieldValue: FormikProps<ExpenseLineItemFormSchema>['setFieldValue']
  initialTaxAmount: Nullable<number>
}

export const useRecomputeTaxAmount = ({
  values,
  setFieldValue,
  initialTaxAmount,
}: UseRecomputeTaxAmountParams) => {
  const { reclaimable_expense_mileage_tax } = useSelector(
    makeSelectFeatureSwitches()
  )

  const isReclaimableMileageTax =
    reclaimable_expense_mileage_tax &&
    values.expenseType === ExpenseTypes.MILEAGE

  const lastComputedTaxAmountRef = useRef<Nullable<number>>(initialTaxAmount)

  const recomputeReclaimableTaxAmount = () => {
    const preset = values.mileageRatePreset
    const recoverableRate = parsePercentageOrZero(
      preset?.tax?.percentage ?? '0'
    )
    const reclaimableTaxAmount =
      recoverableRate <= 0
        ? 0
        : round2(
            ((preset?.advisoryFuelRate ?? 0) *
              (values.distance ?? 0) *
              recoverableRate) /
              (1 + recoverableRate)
          )
    lastComputedTaxAmountRef.current = reclaimableTaxAmount
    if (reclaimableTaxAmount !== values.taxAmount) {
      setFieldValue('taxAmount', reclaimableTaxAmount)
    }
  }

  const recomputeTaxAmountFromSource = () => {
    if (isReclaimableMileageTax) {
      recomputeReclaimableTaxAmount()
      return
    }

    // A flat dollar amount is entered directly, so there is nothing to derive.
    if (values.taxType?.id === '$') return

    let percent: Nullable<number> = null
    if (isPresetTax(values.taxType)) {
      percent = parsePercentageOrZero(values.taxType.percentage)
    } else if (values.customTaxPercentage != null) {
      percent = values.customTaxPercentage / 100
    }
    const computedTaxAmount =
      percent == null ? null : round2((values.subtotal || 0) * percent)
    lastComputedTaxAmountRef.current = computedTaxAmount
    if (computedTaxAmount !== values.taxAmount) {
      setFieldValue('taxAmount', computedTaxAmount)
    }
  }

  // Recompute tax amount when tax type changes
  const isInitialSourceRun = useRef(true)
  useEffect(() => {
    if (isInitialSourceRun.current) {
      isInitialSourceRun.current = false
      return
    }
    if (values.isTaxAmountOverridden) {
      setFieldValue('isTaxAmountOverridden', false)
    }
    recomputeTaxAmountFromSource()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFieldValue is not referentially stable in Formik 2.x
  }, [values.taxType, values.customTaxPercentage, values.mileageRatePreset])

  // Changing subtotal or distance clears any manual override and recomputes.
  const isInitialSubtotalRun = useRef(true)
  useEffect(() => {
    if (isInitialSubtotalRun.current) {
      isInitialSubtotalRun.current = false
      return
    }
    if (isReclaimableMileageTax) return
    if (values.isTaxAmountOverridden) return
    recomputeTaxAmountFromSource()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFieldValue is not referentially stable in Formik 2.x
  }, [values.subtotal, values.distance, values.mileageRate])

  // Recompute reclaimable tax amount when mileage rate preset or distance changes
  const isInitialReclaimableRun = useRef(true)
  useEffect(() => {
    if (!isReclaimableMileageTax) return
    if (isInitialReclaimableRun.current) {
      isInitialReclaimableRun.current = false
      return
    }
    if (values.isTaxAmountOverridden) {
      setFieldValue('isTaxAmountOverridden', false)
    }
    recomputeReclaimableTaxAmount()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFieldValue is not referentially stable in Formik 2.x
  }, [values.distance])

  // A manual edit away from the rate-derived amount marks the line as overridden.
  useEffect(() => {
    const hasPercentageSource =
      isReclaimableMileageTax ||
      isPresetTax(values.taxType) ||
      values.customTaxPercentage != null
    if (
      hasPercentageSource &&
      values.taxAmount != null &&
      values.taxAmount !== lastComputedTaxAmountRef.current &&
      !values.isTaxAmountOverridden
    ) {
      setFieldValue('isTaxAmountOverridden', true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFieldValue is not referentially stable in Formik 2.x
  }, [values.taxAmount])
}
